/**
 * FlowStage derivation — Drive / Process Flow (FE-DRV-*).
 * Drive prefers FlowEnvelope (`drive-operational-flow` meta / `flow-stages`).
 * Process Flow stays on the personal-layer lane proxy (hierarchy roll-up).
 * Do not invent tenant stages in the SPA.
 */

import type { ControlBridgeQueueItem } from '../deriveViewModel';
import type { DriveFlowViewModel, DriveLaneViewModel } from '../personalLayer/driveLaneAggregation';
import { DRIVE_CANONICAL_STATE_ORDER } from '../personalLayer/driveCanonicalLanes';
import { scopeIdMatchesAny } from '../../../utils/scopeIdPlane';
import { normalizeBlockerClass, type BlockerClass } from './clickTarget';
import { deriveActorMarker } from './actorMarker';

export type FlowHealth = 'healthy' | 'warning' | 'critical';
export type FlowStageKind = 'work' | 'handoff' | 'queue' | 'terminal';

export interface FlowPopulation {
  loaded: number;
  expected?: number;
  partial?: boolean;
}

export interface FlowLiveRow {
  itemId: string;
  title?: string;
  dwell?: number | null;
  filterScope?: string;
  companyArtifactRef?: string;
  parentRef?: string;
  reportingPeriodCode?: string;
  reportingPeriodStart?: string;
  reportingPeriodEnd?: string;
}

export interface FlowStage {
  stageId: string;
  label: string;
  wipCount: number;
  health: FlowHealth;
  itemIds: string[];
  items: ControlBridgeQueueItem[];
  live?: FlowLiveRow[];
  hierarchyPath?: string[];
  dwellAvg?: number | null;
  dwellP50?: number | null;
  dwellP90?: number | null;
  ageAvgDays?: number | null;
  dwellUnit?: string;
  throughputStatus?: string;
  kind?: FlowStageKind | string;
  sequenceOrder?: number;
}

export interface FlowModel {
  stages: FlowStage[];
  chokeSummary: string[];
  source: 'manifest' | 'proxy' | 'envelope';
  population?: FlowPopulation;
  /** Period the engine already applied to this envelope (YYYY-MM). */
  period?: string;
}

export interface RollupEnvelopeNode {
  nodeId: string;
  subdomainId?: string;
  label?: string;
  revenueActual?: number;
  costActual?: number;
  reportingPeriodCode?: string;
}

export interface RollupEnvelope {
  schema: string;
  period?: string;
  nodes: RollupEnvelopeNode[];
  population?: FlowPopulation;
}

/** Operator park (ExoPanel block/defer) — not a Drive SM column. */
export const FLOW_PARKED_STAGE_ID = 'parked';

export function isParkedQueueItem(q: ControlBridgeQueueItem): boolean {
  if (String(q.blockedReason || '').trim() || String(q.deferReason || '').trim()) {
    return true;
  }
  const phrase = (q.statusPhrase || '').toLowerCase().trim();
  return phrase === 'blocked' || phrase === 'deferred';
}

function healthFromBlocker(blocker: BlockerClass, heldRatio: number): FlowHealth {
  if (blocker === 'critical' || heldRatio >= 0.5) return 'critical';
  if (blocker === 'warning' || heldRatio >= 0.2) return 'warning';
  return 'healthy';
}

function healthFromLane(lane: DriveLaneViewModel): FlowHealth {
  // Drive entropy visual is active | dormant | undefined — treat undefined as warning signal.
  if (lane.entropy === 'undefined') return 'warning';
  return 'healthy';
}

type ManifestLike = {
  sections: Array<{
    id: string;
    queue?: unknown[];
    meta?: object;
  }>;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function readEnvelopePayload(manifest: ManifestLike): Record<string, unknown> | null {
  for (const id of ['drive-operational-flow', 'flow-stages']) {
    const section = manifest.sections.find((s) => s.id === id);
    const meta = asRecord(section?.meta);
    const envelope = asRecord(meta?.flowEnvelope ?? meta?.flow_envelope);
    if (envelope && Array.isArray(envelope.stages) && envelope.stages.length) {
      return envelope;
    }
  }
  return null;
}

export function parseRollupEnvelopeFromManifest(manifest: ManifestLike): RollupEnvelope | null {
  for (const id of ['drive-operational-flow', 'flow-stages']) {
    const section = manifest.sections.find((s) => s.id === id);
    const meta = asRecord(section?.meta);
    const envelope = asRecord(meta?.rollupEnvelope ?? meta?.rollup_envelope);
    if (!envelope) continue;
    const nodesRaw = Array.isArray(envelope.nodes) ? envelope.nodes : [];
    const nodes: RollupEnvelopeNode[] = [];
    for (const entry of nodesRaw) {
      const row = asRecord(entry);
      if (!row) continue;
      const subdomainId =
        (typeof row.subdomainId === 'string' && row.subdomainId) ||
        (typeof row.subdomain_id === 'string' && row.subdomain_id) ||
        '';
      const nodeId =
        (typeof row.nodeId === 'string' && row.nodeId) ||
        (typeof row.node_id === 'string' && row.node_id) ||
        subdomainId;
      if (!nodeId && !subdomainId) continue;
      const revenue = row.revenueActual ?? row.revenue_actual;
      const cost = row.costActual ?? row.cost_actual;
      nodes.push({
        nodeId: nodeId || subdomainId,
        subdomainId: subdomainId || undefined,
        label: typeof row.label === 'string' ? row.label : undefined,
        revenueActual:
          typeof revenue === 'number' && Number.isFinite(revenue) ? revenue : undefined,
        costActual: typeof cost === 'number' && Number.isFinite(cost) ? cost : undefined,
        reportingPeriodCode:
          (typeof row.reportingPeriodCode === 'string' && row.reportingPeriodCode) ||
          (typeof row.reporting_period_code === 'string' && row.reporting_period_code) ||
          undefined,
      });
    }
    const schema =
      typeof envelope.schema === 'string' && envelope.schema.trim()
        ? envelope.schema
        : 'rollup-envelope/v0.1';
    const period =
      typeof envelope.period === 'string' && envelope.period.trim() ? envelope.period : undefined;
    return { schema, period, nodes, population: parsePopulation(envelope.population) };
  }
  return null;
}

function parseLiveRows(raw: unknown): FlowLiveRow[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: FlowLiveRow[] = [];
  for (const entry of raw) {
    const row = asRecord(entry);
    if (!row) continue;
    const itemId =
      (typeof row.itemId === 'string' && row.itemId) ||
      (typeof row.item_id === 'string' && row.item_id) ||
      '';
    if (!itemId) continue;
    const dwellRaw = row.dwell;
    const filterScope =
      (typeof row.filterScope === 'string' && row.filterScope) ||
      (typeof row.filter_scope === 'string' && row.filter_scope) ||
      '';
    const companyArtifactRef =
      (typeof row.companyArtifactRef === 'string' && row.companyArtifactRef) ||
      (typeof row.company_artifact_ref === 'string' && row.company_artifact_ref) ||
      '';
    const parentRef =
      (typeof row.parentRef === 'string' && row.parentRef) ||
      (typeof row.parent_ref === 'string' && row.parent_ref) ||
      '';
    const reportingPeriodCode =
      (typeof row.reportingPeriodCode === 'string' && row.reportingPeriodCode) ||
      (typeof row.reporting_period_code === 'string' && row.reporting_period_code) ||
      '';
    const reportingPeriodStart =
      (typeof row.reportingPeriodStart === 'string' && row.reportingPeriodStart) ||
      (typeof row.reporting_period_start === 'string' && row.reporting_period_start) ||
      '';
    const reportingPeriodEnd =
      (typeof row.reportingPeriodEnd === 'string' && row.reportingPeriodEnd) ||
      (typeof row.reporting_period_end === 'string' && row.reporting_period_end) ||
      '';
    out.push({
      itemId,
      title: typeof row.title === 'string' ? row.title : undefined,
      dwell: typeof dwellRaw === 'number' && Number.isFinite(dwellRaw) ? dwellRaw : null,
      filterScope: filterScope || undefined,
      companyArtifactRef: companyArtifactRef || undefined,
      parentRef: parentRef || undefined,
      reportingPeriodCode: reportingPeriodCode || undefined,
      reportingPeriodStart: reportingPeriodStart || undefined,
      reportingPeriodEnd: reportingPeriodEnd || undefined,
    });
  }
  return out.length ? out : undefined;
}

function parsePopulation(raw: unknown): FlowPopulation | undefined {
  const row = asRecord(raw);
  if (!row) return undefined;
  const loaded = typeof row.loaded === 'number' ? row.loaded : Number(row.loaded);
  if (!Number.isFinite(loaded)) return undefined;
  const expected =
    typeof row.expected === 'number'
      ? row.expected
      : typeof row.expected === 'string' && row.expected.trim()
        ? Number(row.expected)
        : undefined;
  return {
    loaded,
    expected: Number.isFinite(expected) ? expected : undefined,
    partial: row.partial === true || (Number.isFinite(expected) && loaded < Number(expected)),
  };
}

function stubItemsFromEnvelope(rawItems: unknown, itemIds: string[]): ControlBridgeQueueItem[] {
  const byId = new Map<string, ControlBridgeQueueItem>();
  if (Array.isArray(rawItems)) {
    for (const entry of rawItems) {
      const row = asRecord(entry);
      if (!row) continue;
      const itemId =
        (typeof row.itemId === 'string' && row.itemId) ||
        (typeof row.item_id === 'string' && row.item_id) ||
        '';
      if (!itemId) continue;
      const pickupKind =
        (typeof row.pickupKind === 'string' && row.pickupKind) ||
        (typeof row.pickup_kind === 'string' && row.pickup_kind) ||
        undefined;
      const blockerRaw =
        (typeof row.blockerClass === 'string' && row.blockerClass) ||
        (typeof row.blocker_class === 'string' && row.blocker_class) ||
        undefined;
      const assignedResourceRef =
        (typeof row.assignedResourceRef === 'string' && row.assignedResourceRef) ||
        (typeof row.assigned_resource_ref === 'string' && row.assigned_resource_ref) ||
        undefined;
      byId.set(itemId, {
        itemId,
        title: typeof row.title === 'string' && row.title ? row.title : itemId,
        statusPhrase: 'ACTIVE',
        pickupKind,
        blockerClass: normalizeBlockerClass(blockerRaw) === 'none' ? undefined : normalizeBlockerClass(blockerRaw),
        requiresBridgeSession:
          row.closable === false ? true : row.closable === true ? false : undefined,
        assignedResourceRef,
        actorMarker: deriveActorMarker({
          pickupKind,
          assignedResourceRef,
          assignedResourceLabel:
            typeof row.assignedResourceLabel === 'string'
              ? row.assignedResourceLabel
              : typeof row.assigned_resource_label === 'string'
                ? row.assigned_resource_label
                : undefined,
        }),
      });
    }
  }
  return itemIds.map(
    (id) =>
      byId.get(id) ?? {
        itemId: id,
        title: id,
        statusPhrase: 'ACTIVE',
      }
  );
}

function dwellUnitSuffix(unit: string | undefined, compact: boolean): string {
  if (unit === 'days') return compact ? 'd' : ' days';
  if (unit === 'hours') return compact ? 'h' : ' hours';
  return unit ? ` ${unit}` : '';
}

function chokeLine(stage: FlowStage): string {
  const dwell =
    typeof stage.dwellAvg === 'number' && Number.isFinite(stage.dwellAvg)
      ? `, dwell ${stage.dwellAvg}${dwellUnitSuffix(stage.dwellUnit, true)}`
      : typeof stage.ageAvgDays === 'number' && Number.isFinite(stage.ageAvgDays)
        ? `, age ${stage.ageAvgDays}d`
        : '';
  return `${stage.label} (WIP ${stage.wipCount}${dwell})`;
}

function flowModelFromEnvelope(envelope: Record<string, unknown>): FlowModel | null {
  const rawStages = Array.isArray(envelope.stages) ? envelope.stages : [];
  const stages: FlowStage[] = [];
  for (const entry of rawStages) {
    const row = asRecord(entry);
    if (!row) continue;
    const stageId =
      (typeof row.node_id === 'string' && row.node_id) ||
      (typeof row.nodeId === 'string' && row.nodeId) ||
      (typeof row.stageId === 'string' && row.stageId) ||
      '';
    if (!stageId) continue;
    const seqRaw = row.sequence_order ?? row.sequenceOrder;
    const sequenceOrder = typeof seqRaw === 'number' ? seqRaw : undefined;
    const wip =
      typeof row.wip === 'number'
        ? row.wip
        : typeof row.wipCount === 'number'
          ? row.wipCount
          : 0;
    const finite = (value: unknown): number | null =>
      typeof value === 'number' && Number.isFinite(value) ? value : null;
    const dwellRaw = row.dwell_avg ?? row.dwellAvg;
    const dwellAvg = finite(dwellRaw);
    const roll = String(row.roll_state ?? row.rollState ?? row.health ?? '').toLowerCase();
    const health: FlowHealth =
      roll === 'critical' || roll === 'warning' || roll === 'healthy' ? roll : 'healthy';
    const itemIds = Array.isArray(row.item_ids)
      ? row.item_ids.filter((x): x is string => typeof x === 'string')
      : Array.isArray(row.itemIds)
        ? row.itemIds.filter((x): x is string => typeof x === 'string')
        : [];
    const label =
      (typeof row.label === 'string' && row.label) ||
      (sequenceOrder != null ? String(sequenceOrder) : stageId);
    stages.push({
      stageId,
      label,
      wipCount: wip,
      health,
      itemIds,
      items: stubItemsFromEnvelope(row.items, itemIds),
      live: parseLiveRows(row.live),
      dwellAvg,
      dwellP50: finite(row.dwell_p50 ?? row.dwellP50),
      dwellP90: finite(row.dwell_p90 ?? row.dwellP90),
      ageAvgDays: finite(row.age_avg_days ?? row.ageAvgDays),
      dwellUnit:
        typeof row.dwell_unit === 'string'
          ? row.dwell_unit
          : typeof row.dwellUnit === 'string'
            ? row.dwellUnit
            : 'days',
      throughputStatus:
        typeof row.throughput_status === 'string'
          ? row.throughput_status
          : typeof row.throughputStatus === 'string'
            ? row.throughputStatus
            : undefined,
      kind: typeof row.kind === 'string' ? row.kind : undefined,
      sequenceOrder,
    });
  }
  if (!stages.length) return null;
  const chokeSummary = stages
    .filter((s) => s.health === 'critical' || s.health === 'warning')
    .map((s) => chokeLine(s));
  return {
    stages,
    chokeSummary,
    source: 'envelope',
    population: parsePopulation(envelope.population),
    period:
      typeof envelope.period === 'string' && envelope.period.trim()
        ? envelope.period.trim()
        : undefined,
  };
}

/** Prefer FlowEnvelope payload; fall back to `flow-stages` queue. */
export function parseFlowEnvelopeFromManifest(manifest: ManifestLike): FlowModel | null {
  const envelope = readEnvelopePayload(manifest);
  if (envelope) return flowModelFromEnvelope(envelope);
  return parseFlowStagesFromManifest(manifest);
}

/** Parse FlowStage[] from render-manifest section `flow-stages`. */
export function parseFlowStagesFromManifest(manifest: ManifestLike): FlowModel | null {
  const section = manifest.sections.find((s) => s.id === 'flow-stages');
  if (!section?.queue?.length) return null;
  const meta = asRecord(section.meta);
  const fromMetaEnvelope = asRecord(meta?.flowEnvelope ?? meta?.flow_envelope);
  if (fromMetaEnvelope && Array.isArray(fromMetaEnvelope.stages) && fromMetaEnvelope.stages.length) {
    return flowModelFromEnvelope(fromMetaEnvelope);
  }

  const stages: FlowStage[] = section.queue.map((raw, index) => {
    const row = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const stageId =
      (typeof row.stageId === 'string' && row.stageId) ||
      (typeof row.itemId === 'string' && row.itemId) ||
      `stage-${index}`;
    const label =
      (typeof row.label === 'string' && row.label) ||
      (typeof row.title === 'string' && row.title) ||
      stageId;
    const wipCount =
      typeof row.wipCount === 'number'
        ? row.wipCount
        : typeof row.wip_count === 'number'
          ? row.wip_count
          : typeof row.wip === 'number'
            ? row.wip
            : Array.isArray(row.itemIds)
              ? row.itemIds.length
              : 0;
    const blocker = normalizeBlockerClass(
      typeof row.blockerClass === 'string'
        ? row.blockerClass
        : typeof row.blocker_class === 'string'
          ? row.blocker_class
          : typeof row.health === 'string'
            ? row.health
            : undefined
    );
    const healthRaw = typeof row.health === 'string' ? row.health.toLowerCase() : '';
    const health: FlowHealth =
      healthRaw === 'critical' || healthRaw === 'warning' || healthRaw === 'healthy'
        ? healthRaw
        : healthFromBlocker(blocker, 0);
    const itemIds = Array.isArray(row.itemIds)
      ? row.itemIds.filter((x): x is string => typeof x === 'string')
      : Array.isArray(row.item_ids)
        ? (row.item_ids as unknown[]).filter((x): x is string => typeof x === 'string')
        : [];
    const dwellRaw = row.dwellAvg ?? row.dwell_avg;
    const seqRaw = row.sequenceOrder ?? row.sequence_order;
    return {
      stageId,
      label,
      wipCount: wipCount || itemIds.length,
      health,
      itemIds,
      items: stubItemsFromEnvelope(row.items, itemIds),
      dwellAvg: typeof dwellRaw === 'number' && Number.isFinite(dwellRaw) ? dwellRaw : null,
      dwellP50: typeof row.dwellP50 === 'number' ? row.dwellP50 : null,
      dwellP90: typeof row.dwellP90 === 'number' ? row.dwellP90 : null,
      ageAvgDays: typeof row.ageAvgDays === 'number' ? row.ageAvgDays : null,
      dwellUnit: typeof row.dwellUnit === 'string' ? row.dwellUnit : 'days',
      throughputStatus: typeof row.throughputStatus === 'string' ? row.throughputStatus : undefined,
      kind: typeof row.kind === 'string' ? row.kind : undefined,
      sequenceOrder: typeof seqRaw === 'number' ? seqRaw : undefined,
    };
  });

  const chokeSummary = stages
    .filter((s) => s.health === 'critical' || s.health === 'warning')
    .map((s) => chokeLine(s));
  const source = String(meta?.source || '').toLowerCase() === 'envelope' ? 'envelope' : 'manifest';

  return {
    stages,
    chokeSummary,
    source,
    population: parsePopulation(meta?.population),
  };
}

/**
 * Drive: prefer FlowEnvelope on the view model; else lane-proxy stages.
 * Process Flow must not call this — it would replace hierarchy roll-up with OWI stages.
 */
export function resolveDriveFlowModel(
  vm: { flowModel?: FlowModel | null },
  driveFlow: DriveFlowViewModel | null | undefined,
  queueItems: ControlBridgeQueueItem[]
): FlowModel | null {
  if (vm.flowModel?.stages?.length) return vm.flowModel;
  return deriveFlowStagesProxy(driveFlow, queueItems);
}

/** Process Flow: hierarchy lane proxy only. Envelope stays on Drive (D1). */
export function resolveProcessFlowModel(
  driveFlow: DriveFlowViewModel | null | undefined,
  queueItems: ControlBridgeQueueItem[]
): FlowModel | null {
  return deriveFlowStagesProxy(driveFlow, queueItems);
}

function pressureLevel(values: number[]): 'none' | FlowHealth {
  const usable = values.filter((v) => Number.isFinite(v));
  if (!usable.length) return 'none';
  const ranked = [...usable].sort((a, b) => b - a);
  const mx = ranked[0];
  if (mx <= 0) return 'none';
  if (ranked.length === 1) return 'warning';
  const second = ranked[1];
  if (second > 0 && mx >= second * 1.5) return 'critical';
  if (mx > second) return 'warning';
  return 'none';
}

function worseHealth(current: FlowHealth, incoming: 'none' | FlowHealth): FlowHealth {
  const order: Record<string, number> = { healthy: 0, none: 0, warning: 1, critical: 2 };
  return (order[incoming] ?? 0) > (order[current] ?? 0) ? (incoming as FlowHealth) : current;
}

function assignEnvelopeRollStates(stages: FlowStage[]): void {
  const dwells = stages
    .map((s) => s.dwellAvg)
    .filter((d): d is number => typeof d === 'number' && Number.isFinite(d));
  const wips = stages.map((s) => s.wipCount);
  const dwellLevel = pressureLevel(dwells);
  const wipLevel = pressureLevel(wips);
  const dwellMx = dwells.length ? Math.max(...dwells) : null;
  const wipMx = wips.length ? Math.max(...wips) : null;
  for (const stage of stages) {
    let health: FlowHealth = 'healthy';
    if (dwellMx != null && stage.dwellAvg === dwellMx) health = worseHealth(health, dwellLevel);
    if (wipMx != null && stage.wipCount === wipMx) health = worseHealth(health, wipLevel);
    stage.health = health;
  }
}

function liveMatchesScope(row: FlowLiveRow, wanted: string[]): boolean {
  for (const tok of [row.filterScope, row.companyArtifactRef, row.parentRef]) {
    if (tok && scopeIdMatchesAny(tok, wanted)) return true;
  }
  return false;
}

function periodTokenEquals(code: string, needle: string): boolean {
  const left = code.trim();
  const right = needle.trim();
  if (!left || !right) return false;
  if (left === right) return true;
  if (left.replace(/-/g, '') === right.replace(/-/g, '')) return true;
  return right.length === 7 && right[4] === '-' && (left.startsWith(`${right}-`) || left.startsWith(`${right}T`));
}

function monthBoundsFromYyyyMm(period: string): { start: string; end: string } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(period.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  const last = new Date(year, month, 0).getDate();
  const mm = String(month).padStart(2, '0');
  return { start: `${year}-${mm}-01`, end: `${year}-${mm}-${String(last).padStart(2, '0')}` };
}

function liveRowHasPeriodFields(row: FlowLiveRow): boolean {
  return Boolean(
    (row.reportingPeriodCode || '').trim() ||
      (row.reportingPeriodStart || '').trim() ||
      (row.reportingPeriodEnd || '').trim()
  );
}

/** Keep unbounded OWI; drop bounded OWI outside the YYYY-MM window (engine overlap). */
export function liveMatchesPeriod(row: FlowLiveRow, period: string | null | undefined): boolean {
  const needle = (period || '').trim();
  if (!needle) return true;
  const code = (row.reportingPeriodCode || '').trim();
  if (code && periodTokenEquals(code, needle)) return true;
  const start = (row.reportingPeriodStart || '').trim().slice(0, 10);
  const end = (row.reportingPeriodEnd || '').trim().slice(0, 10);
  if (!code && !start && !end) return true;
  if (start.startsWith(needle) || (end && end.startsWith(needle))) return true;
  const window = monthBoundsFromYyyyMm(needle);
  if (!window) return true;
  if (start && end) return start <= window.end && end >= window.start;
  if (start) return window.start <= start && start <= window.end;
  if (end) return window.start <= end && end <= window.end;
  return false;
}

function rebuildEnvelopeStages(model: FlowModel, keep: (row: FlowLiveRow) => boolean): FlowModel {
  const stages = model.stages
    .map((stage) => {
      const live = (stage.live ?? []).filter(keep);
      const dwellVals = live
        .map((r) => r.dwell)
        .filter((d): d is number => typeof d === 'number' && Number.isFinite(d));
      const dwellAvg = dwellVals.length
        ? Math.round((dwellVals.reduce((a, b) => a + b, 0) / dwellVals.length) * 1000) / 1000
        : null;
      const capped = live.slice(0, 12);
      return {
        ...stage,
        live,
        wipCount: live.length,
        dwellAvg,
        itemIds: capped.map((r) => r.itemId),
        items: capped.map((r) => ({
          itemId: r.itemId,
          title: r.title || r.itemId,
          statusPhrase: 'ACTIVE',
        })),
      };
    })
    .filter((stage) => stage.wipCount > 0);
  assignEnvelopeRollStates(stages);
  return {
    ...model,
    stages,
    chokeSummary: stages
      .filter((s) => s.health === 'critical' || s.health === 'warning')
      .map((s) => chokeLine(s)),
  };
}

/** Sidebar scope: if any live OWI matches, shrink stages; else keep company-wide envelope. */
export function filterFlowModelByScope(model: FlowModel, scopeKeys: string[]): FlowModel {
  if (model.source !== 'envelope') return model;
  const wanted = scopeKeys.map((k) => k.trim()).filter(Boolean);
  if (!wanted.length) return model;
  const hasLive = model.stages.some((s) => (s.live?.length ?? 0) > 0);
  if (!hasLive) return model;
  const anyHit = model.stages.some((s) => (s.live ?? []).some((row) => liveMatchesScope(row, wanted)));
  if (!anyHit) return model;
  return rebuildEnvelopeStages(model, (row) => liveMatchesScope(row, wanted));
}

/** View / Drive period: unbounded live rows stay; bounded rows must match YYYY-MM. */
export function filterFlowModelByPeriod(model: FlowModel, period: string | null | undefined): FlowModel {
  if (model.source !== 'envelope') return model;
  const needle = (period || '').trim();
  if (!needle) return model;
  if ((model.period || '').trim() === needle) return model;
  const hasLive = model.stages.some((s) => (s.live?.length ?? 0) > 0);
  if (!hasLive) return model;
  const anyPeriodFields = model.stages.some((s) => (s.live ?? []).some(liveRowHasPeriodFields));
  if (!anyPeriodFields) return model;
  return rebuildEnvelopeStages(model, (row) => liveMatchesPeriod(row, needle));
}

/** Period the engine applied, else product-flow view-definition / rollup envelope. */
export function parseDriveViewPeriod(manifest: ManifestLike): string | undefined {
  const envelope = readEnvelopePayload(manifest);
  if (typeof envelope?.period === 'string' && envelope.period.trim()) {
    return envelope.period.trim();
  }
  for (const id of ['drive-operational-flow', 'flow-stages']) {
    const section = manifest.sections.find((s) => s.id === id);
    const meta = asRecord(section?.meta);
    if (!meta) continue;
    const viewEval = asRecord(meta.viewDefinition ?? meta.view_definition);
    const nestedView = asRecord(viewEval?.view);
    for (const raw of [viewEval?.period, nestedView?.period, nestedView?.reporting_period]) {
      if (typeof raw === 'string' && raw.trim()) return raw.trim();
    }
    const rollup = asRecord(meta.rollupEnvelope ?? meta.rollup_envelope);
    if (typeof rollup?.period === 'string' && rollup.period.trim()) return rollup.period.trim();
  }
  return undefined;
}

/**
 * Items mode rows for a stage. Prefer live ids/titles when present, but keep
 * envelope/queue stub fields (actorMarker, blockerClass) so Drive chrome and
 * ClickTarget stay wired (FE-DRV-8 / FE-EXO-6b).
 */
export function stageItemsForToggle(stage: FlowStage): ControlBridgeQueueItem[] {
  if (stage.live?.length) {
    const byId = new Map(stage.items.map((item) => [item.itemId, item]));
    return stage.live.map((row) => {
      const stub = byId.get(row.itemId);
      return {
        ...(stub ?? {
          itemId: row.itemId,
          title: row.title || row.itemId,
          statusPhrase: 'ACTIVE',
        }),
        itemId: row.itemId,
        title: row.title || stub?.title || row.itemId,
        statusPhrase: stub?.statusPhrase || 'ACTIVE',
      };
    });
  }
  return stage.items;
}

/** Stages named in the Pressure / choke summary (FE-DRV-3). */
export function pressureStagesFromModel(model: FlowModel): FlowStage[] {
  return model.stages.filter((s) => s.health === 'critical' || s.health === 'warning');
}

/**
 * Prefer a critical/held closable item for Drive → Exo entry (FE-EXO-6b choke link).
 * Falls back to first closable, then first listed id.
 */
export function pickDriveExoItemId(stage: FlowStage): string | null {
  const listed = stageItemsForToggle(stage);
  const criticalClosable = listed.find(
    (item) => item.blockerClass === 'critical' && item.requiresBridgeSession !== true
  );
  if (criticalClosable) return criticalClosable.itemId;
  const anyCritical = listed.find((item) => item.blockerClass === 'critical');
  if (anyCritical) return anyCritical.itemId;
  const closable = listed.find((item) => item.requiresBridgeSession !== true);
  if (closable) return closable.itemId;
  if (listed[0]?.itemId) return listed[0].itemId;
  return stage.itemIds[0] ?? null;
}

export function itemIsHeldOnDrive(item: ControlBridgeQueueItem): boolean {
  return item.blockerClass === 'critical';
}

export function itemCanOpenExoFromDrive(item: ControlBridgeQueueItem): boolean {
  if (!item.itemId) return false;
  if (item.requiresBridgeSession === true) return false;
  return item.blockerClass === 'critical';
}

export function itemFromFlowModel(
  model: FlowModel | null | undefined,
  itemId: string
): ControlBridgeQueueItem | undefined {
  if (!model || !itemId) return undefined;
  for (const stage of model.stages) {
    const hit = stageItemsForToggle(stage).find((item) => item.itemId === itemId);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * Proxy FlowStage model from Drive lane aggregation (canonical state columns).
 * Not inventing business stages — uses existing Drive state order as stand-in until contract #3.
 */
export function deriveFlowStagesProxy(
  flow: DriveFlowViewModel | null | undefined,
  queueItems: ControlBridgeQueueItem[] = []
): FlowModel | null {
  if (!flow?.lanes?.length) return null;

  // Aggregate WIP across lanes by canonical state.
  const byState: Record<string, number> = {};
  for (const state of DRIVE_CANONICAL_STATE_ORDER) {
    byState[state] = 0;
  }
  for (const lane of flow.lanes) {
    for (const type of lane.types) {
      for (const [state, count] of Object.entries(type.byState)) {
        byState[state] = (byState[state] ?? 0) + (count ?? 0);
      }
    }
  }

  const parkedItems = queueItems.filter(isParkedQueueItem);
  const inFlowItems = queueItems.filter((q) => !isParkedQueueItem(q));
  const queueBacked = queueItems.length > 0;

  const stages: FlowStage[] = DRIVE_CANONICAL_STATE_ORDER.map((state) => {
    const laneWip = byState[state] ?? 0;
    const items = inFlowItems.filter((q) => {
      const phrase = (q.statusPhrase || '').toLowerCase();
      if (state === 'identified') return /identif|new|prospect/.test(phrase);
      if (state === 'created') return /creat/.test(phrase) && !/identif/.test(phrase);
      if (state === 'defined') return /defin|draft/.test(phrase);
      if (state === 'active') return /active|progress|in.?flight/.test(phrase);
      if (state === 'posted') return /post|complete|closed|done/.test(phrase);
      return false;
    });
    // When CB queue is present, stage WIP must match listed (non-parked) items — not Drive byState,
    // which still counts ACTIVE rows that ExoPanel parked via properties.
    const wipCount = queueBacked ? items.length : laneWip || items.length;
    const held = items.filter(
      (i) =>
        normalizeBlockerClass(
          (i as ControlBridgeQueueItem & { blockerClass?: string }).blockerClass
        ) === 'critical'
    ).length;
    const heldRatio = items.length ? held / items.length : 0;
    const laneHealth = flow.lanes.reduce<FlowHealth>((acc, lane) => {
      const h = healthFromLane(lane);
      if (h === 'critical' || acc === 'critical') return 'critical';
      if (h === 'warning' || acc === 'warning') return 'warning';
      return 'healthy';
    }, 'healthy');
    const health =
      heldRatio > 0 ? healthFromBlocker(held > 0 ? 'critical' : 'none', heldRatio) : laneHealth;

    return {
      stageId: state,
      label: state.charAt(0).toUpperCase() + state.slice(1),
      wipCount,
      health: wipCount === 0 ? 'healthy' : health,
      itemIds: items.map((i) => i.itemId),
      items,
      hierarchyPath: [flow.domainLabel],
    };
  });

  if (parkedItems.length > 0) {
    stages.push({
      stageId: FLOW_PARKED_STAGE_ID,
      label: 'Parked',
      wipCount: parkedItems.length,
      health: 'warning',
      itemIds: parkedItems.map((i) => i.itemId),
      items: parkedItems,
      hierarchyPath: [flow.domainLabel],
    });
  }

  const chokeSummary = stages
    .filter(
      (s) =>
        s.stageId !== FLOW_PARKED_STAGE_ID && s.health !== 'healthy' && s.wipCount > 0
    )
    .map((s) => `${s.label} (${s.wipCount})`);

  return { stages, chokeSummary, source: 'proxy' };
}

export function flowSummaryTiles(model: FlowModel): Array<{ key: string; label: string; value: string }> {
  const inFlow = model.stages
    .filter((s) => s.stageId !== 'posted' && s.stageId !== FLOW_PARKED_STAGE_ID)
    .reduce((n, s) => n + s.wipCount, 0);
  const parked =
    model.stages.find((s) => s.stageId === FLOW_PARKED_STAGE_ID)?.wipCount ?? 0;
  const blocked = model.stages
    .filter(
      (s) => s.stageId !== FLOW_PARKED_STAGE_ID && s.health !== 'healthy'
    )
    .reduce((n, s) => n + s.wipCount, 0);
  const peakDwell = model.stages.reduce<FlowStage | null>((best, stage) => {
    if (typeof stage.dwellAvg !== 'number' || !Number.isFinite(stage.dwellAvg)) return best;
    if (!best || (best.dwellAvg ?? 0) < stage.dwellAvg) return stage;
    return best;
  }, null);
  const tiles: Array<{ key: string; label: string; value: string }> = [
    { key: 'in_flow', label: 'Moving', value: String(inFlow) },
    { key: 'parked', label: 'Parked', value: String(parked) },
    { key: 'blocked', label: 'Held', value: String(blocked) },
  ];
  if (peakDwell && typeof peakDwell.dwellAvg === 'number') {
    tiles.push({
      key: 'dwell',
      label: 'Longest wait',
      value: `${peakDwell.dwellAvg}${dwellUnitSuffix(peakDwell.dwellUnit, true)}`,
    });
  }
  return tiles;
}

export function flowPopulationCaption(model: FlowModel): string | null {
  const pop = model.population;
  if (!pop) return null;
  if (typeof pop.expected === 'number' && Number.isFinite(pop.expected)) {
    const prefix = pop.partial ? 'Partial population' : 'Population';
    return `${prefix} ${pop.loaded} / ${pop.expected}`;
  }
  return `${pop.loaded} loaded`;
}
